'use client'

import { AnimatePresence, motion, MotionStyle } from 'motion/react'
import Bug from 'public/assets/bug.svg'
import Controls from 'public/assets/controls.svg'
import Heart from 'public/assets/heart.svg'
import Lamp from 'public/assets/lamp.svg'
import Window from 'public/assets/window.svg'
import { useEffect, useRef, useState } from 'react'

const ICONS = [
  { key: 'bug', Icon: Bug },
  { key: 'controls', Icon: Controls },
  { key: 'heart', Icon: Heart },
  { key: 'lamp', Icon: Lamp },
  { key: 'window', Icon: Window },
]
const DISTANCE = 60
const DISTANCE_MARGIN = 15
const VISIBLE_MS = 100

const animation = {
  initial: { scale: 0, opacity: 0 },
  animate: { scale: 1, opacity: 1, rotate: 0, transition: { duration: 0.1 } },
  exit: { scale: 0, opacity: 0, rotate: 30, transition: { duration: 0.5, delay: 0.3 } },
}

const iconStyle: MotionStyle = {
  position: 'fixed',
  zIndex: 5,
  width: 84,
  height: 84,
}

type ActiveIcon = { index: number; x: number; y: number }

// Spawns icons along the mouse path inside the parent element
const FloatingIcons = () => {
  const anchorRef = useRef<HTMLSpanElement>(null)
  const [activeIcon, setActiveIcon] = useState<ActiveIcon | null>(null)

  useEffect(() => {
    const container = anchorRef.current?.parentElement
    if (!container) return

    let movement = 0
    let iconCount = 0
    let iconShown = false
    let hideTimeout: ReturnType<typeof setTimeout> | undefined

    const handleMouseMove = (e: MouseEvent) => {
      movement += Math.round(Math.hypot(e.movementX, e.movementY))
      if (iconShown || movement < DISTANCE || movement % DISTANCE >= DISTANCE_MARGIN) return

      iconShown = true
      setActiveIcon({ index: iconCount++ % ICONS.length, x: e.clientX, y: e.clientY })
      hideTimeout = setTimeout(() => {
        setActiveIcon(null)
        iconShown = false
      }, VISIBLE_MS)
    }

    container.addEventListener('mousemove', handleMouseMove)

    return () => {
      container.removeEventListener('mousemove', handleMouseMove)
      clearTimeout(hideTimeout)
    }
  }, [])

  const active = activeIcon ? ICONS[activeIcon.index] : null

  return (
    <>
      <span ref={anchorRef} hidden />
      <AnimatePresence>
        {active && activeIcon && (
          <motion.div key={active.key} style={{ ...iconStyle, left: activeIcon.x, top: activeIcon.y }} {...animation}>
            <active.Icon />
          </motion.div>
        )}
      </AnimatePresence>
    </>
  )
}

export default FloatingIcons
